import {
  DEFAULT_EDUCATION_SETTINGS,
  SCHEMA_VERSION,
  STORAGE_KEY,
} from "../types";

function createDefaultState(userId = null) {
  return {
    schemaVersion: SCHEMA_VERSION,
    userId,
    lastSeenReleaseId: null,
    lastSeenVersion: null,
    releases: {},
    optedOutReleases: [],
    dismissedFeatureIds: [],
    settings: { ...DEFAULT_EDUCATION_SETTINGS },
  };
}

function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Schema v2: auto-launch release education / Ashley brief default OFF.
 * Existing v1 (and unversioned) state inherited the old ON defaults into
 * localStorage even when the user never changed Preferences — force those
 * opt-in flags off on upgrade. Users can re-enable from Preferences.
 */
function migrateEducationSettings(settings, previousSchemaVersion) {
  const next = { ...settings };
  if (previousSchemaVersion < 2) {
    next.autoTourEnabled = false;
    next.ashleyBriefEnabled = false;
  }
  return next;
}

export function normalizeEducationState(raw, userId = null) {
  if (!isObject(raw)) {
    return createDefaultState(userId);
  }

  const previousSchemaVersion = Number(raw.schemaVersion) || 0;
  const settings = migrateEducationSettings(
    {
      ...DEFAULT_EDUCATION_SETTINGS,
      ...(isObject(raw.settings) ? raw.settings : {}),
    },
    previousSchemaVersion,
  );

  return {
    schemaVersion: SCHEMA_VERSION,
    userId: raw.userId ?? userId,
    lastSeenReleaseId: raw.lastSeenReleaseId ?? null,
    lastSeenVersion: raw.lastSeenVersion ?? null,
    releases: isObject(raw.releases) ? { ...raw.releases } : {},
    optedOutReleases: Array.isArray(raw.optedOutReleases)
      ? [...raw.optedOutReleases]
      : [],
    dismissedFeatureIds: Array.isArray(raw.dismissedFeatureIds)
      ? [...raw.dismissedFeatureIds]
      : [],
    settings,
  };
}

export function readEducationState(userId) {
  if (typeof window === "undefined") {
    return createDefaultState(userId);
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return createDefaultState(userId);
    }

    const parsed = JSON.parse(raw);
    if (parsed.userId && userId && parsed.userId !== userId) {
      return createDefaultState(userId);
    }

    const previousSchemaVersion = Number(parsed?.schemaVersion) || 0;
    const normalized = normalizeEducationState(parsed, userId);
    if (previousSchemaVersion < SCHEMA_VERSION) {
      writeEducationState(normalized);
    }
    return normalized;
  } catch (error) {
    console.warn("[ProductEducation] Failed to read persistence state", error);
    return createDefaultState(userId);
  }
}

export function writeEducationState(state) {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn("[ProductEducation] Failed to write persistence state", error);
  }
}

export function clearEducationStateForUser() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(STORAGE_KEY);
}

export function updateEducationState(userId, updater) {
  const current = readEducationState(userId);
  const next = updater(current);
  const normalized = normalizeEducationState(
    { ...next, userId: userId ?? next.userId ?? null },
    userId,
  );
  writeEducationState(normalized);
  return normalized;
}

export function getReleaseRecord(state, releaseId) {
  return state.releases[releaseId] || null;
}

export function upsertReleaseRecord(state, releaseId, patch) {
  const existing = getReleaseRecord(state, releaseId) || {
    status: "not_started",
  };

  return {
    ...state,
    releases: {
      ...state.releases,
      [releaseId]: {
        ...existing,
        ...patch,
        updatedAt: new Date().toISOString(),
      },
    },
  };
}
